import React, { useState, useEffect } from "react";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { DesktopBackLink } from "./navigation/ScreenBackButton";
import { supabase } from "../supabase";
import { formatHoursMinutes } from "../helpers/timeHelpers";
import { formatSessionShiftLabel } from "../helpers/dateTimeHelpers";
import { getActiveProjects } from "../helpers/projectVisibility";
import { getScopedCachedProjects } from "../helpers/offlineCacheScope";
import { isOffline } from "../helpers/connectivity";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY, DATA_SCOPE } from "../helpers/permissions";
import { loadScopedActiveProjects } from "../helpers/operationalQueryAdapters";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  loadScopedUsers,
  resolveOperationalOrganizationId,
  resolveReportScopeUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../helpers/scopedQueries";
import { shouldSkipSupabase } from "../helpers/guestMode";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { ReportBuilderDesktop } from "./intelligence/IntelligenceDesktop";

/**
 * ReportBuilderScreen
 * UI-only report builder — no backend calls, no data fetching.
 * Supports "Hours By Project" and "Hours By Date Range" report types
 * with conditional fields rendered per selection.
 */
export function ReportBuilderScreen({ onBack, currentUser }) {
  const desktopNav = useDesktopNav();
  const isDesktop = useIsDesktopViewport();
  const { scope, directReportIds } = usePermissions();
  const timelogScope = scope(CAPABILITY.TIMELOG_VIEW);
  const showTeamReportScope =
    timelogScope === DATA_SCOPE.TEAM || timelogScope === DATA_SCOPE.ORGANIZATION;
  const showOrgReportScope = timelogScope === DATA_SCOPE.ORGANIZATION;

  const [reportType, setReportType] = useState("");
  const [project, setProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [reportResult, setReportResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportScope, setReportScope] = useState("my_time");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employees, setEmployees] = useState([]);

  const isHoursByProject = reportType === "hours_by_project";
  const isHoursByDateRange = reportType === "hours_by_date_range";
  async function handleGenerateReport() {
    setIsGenerating(true);
    setReportResult(null);
    if (currentUser?.isGuest) {
      if (reportType === "hours_by_project") {
        setReportResult({
          type: "Team Project Report",
          title:
  projects.find((p) => p.project_id === project)?.project_name ||
  "Selected Project",
          totalHours: 42.5,
          entries: 12,
          logs: [
            {
              employee: "Mike Tremblay",
              work_completed: "Completed Borehole #17",
              total_hours: 8,
              project_name: "Highway 17 Soil Investigation",
            },
            {
              employee: "Sarah Martin",
              work_completed: "Site Mobilization",
              total_hours: 3.5,
              project_name: "Highway 17 Soil Investigation",
            },
          ],
        });

        setIsGenerating(false);
        return;
      }

      if (reportType === "hours_by_date_range") {
        setReportResult({
          type: "Operations Snapshot",
          title: "June Operations Summary",
          totalHours: 142,
          entries: 38,
          grouped: {
            "Highway 17 Soil Investigation": 42.5,
            "Municipal Water Study": 38,
            "Fleet Operations": 31.5,
            "Smith Quarry Investigation": 30,
          },
        });

        setIsGenerating(false);
        return;
      }
    }
    try {
      const organizationId = await resolveOperationalOrganizationId(
        currentUser,
        supabase,
        { directReportIds },
      );

      if (reportType === "hours_by_project") {
        const selectedProject = projects.find((p) => p.project_id === project);
        let query = selectOperationalTimeLogs(
          supabase,
          `
project_id,
project_name,
date_iso,
total_hours,
total_seconds,
break_seconds,
net_work_seconds,
start_time,
end_time,
employee,
work_completed,
work_category
`,
        ).eq("project_id", project);

        const reportUserIds = await resolveReportScopeUserIdsForQuery(
          supabase,
          currentUser,
          reportScope,
          selectedEmployee,
          { directReportIds },
        );

        query = applyUserIdsToQuery(query, reportUserIds);
        query = applyOperationalTimeLogOrgScope(query, organizationId);

        const { data, error } = await query;

        if (error) throw error;

        const totalHours = (data || []).reduce(
          (sum, row) => sum + Number(row.total_hours || 0),
          0,
        );

        setReportResult({
          type: "Hours By Project",
          title: selectedProject?.project_name || "Selected Project",
          totalHours,
          entries: data?.length || 0,
          logs: data || [],
        });
      } else if (reportType === "hours_by_date_range") {
        let query = selectOperationalTimeLogs(
          supabase,
          "project_name, total_hours, date_iso",
        )
          .gte("date_iso", startDate)
          .lte("date_iso", endDate);

        const reportUserIds = await resolveReportScopeUserIdsForQuery(
          supabase,
          currentUser,
          reportScope,
          selectedEmployee,
          { directReportIds },
        );

        query = applyUserIdsToQuery(query, reportUserIds);
        query = applyOperationalTimeLogOrgScope(query, organizationId);

        const { data, error } = await query;

        if (error) throw error;

        const grouped = {};

        (data || []).forEach((row) => {
          const name = row.project_name || "Unknown Project";

          grouped[name] = (grouped[name] || 0) + Number(row.total_hours || 0);
        });

        const totalHours = Object.values(grouped).reduce(
          (sum, hours) => sum + hours,
          0,
        );

        setReportResult({
          type: "Hours By Date Range",
          title: `${new Date(startDate + "T12:00:00").toLocaleDateString(
  "en-US",
  {
    month: "long",
    day: "numeric",
    year: "numeric",
  }
)} → ${new Date(endDate + "T12:00:00").toLocaleDateString(
  "en-US",
  {
    month: "long",
    day: "numeric",
    year: "numeric",
  }
)}`,
          totalHours,
          entries: data?.length || 0,
          grouped,
        });
      }
    } catch (err) {
      console.error("REPORT ERROR:", err);
      alert("Unable to generate report.");
    } finally {
      setIsGenerating(false);
    }
  }
  useEffect(() => {
    loadProjects();
    loadEmployees();
  }, [currentUser?.supabase_user_id, directReportIds]);

  async function loadEmployees() {
    if (shouldSkipSupabase(currentUser)) {
      setEmployees([]);
      return;
    }

    try {
      if (!currentUser || !showTeamReportScope) {
        setEmployees([]);
        return;
      }

      const data = await loadScopedUsers(
        supabase,
        currentUser,
        CAPABILITY.TIMELOG_VIEW,
        "supabase_user_id, employee_name",
        { directReportIds },
      );
      setEmployees(data);
    } catch (err) {
      console.error("EMPLOYEE LOAD ERROR:", err);
      setEmployees([]);
    }
  }

  async function loadProjects() {
    if (currentUser?.isGuest) {
  setProjects([
    {
      project_id: "demo-1",
      project_name: "Highway 17 Soil Investigation",
    },
    {
      project_id: "demo-2",
      project_name: "Municipal Water Study",
    },
    {
      project_id: "demo-3",
      project_name: "Fleet Operations",
    },
    {
      project_id: "demo-4",
      project_name: "Smith Quarry Investigation",
    },
  ]);

  return;
}
    if (isOffline()) {
      setProjects(getActiveProjects(getScopedCachedProjects(currentUser, directReportIds)));
      return;
    }

    try {
      const data = await loadScopedActiveProjects(
        supabase,
        currentUser,
        { directReportIds },
      );
      console.log("PROJECTS LOADED:", data);
      setProjects(getActiveProjects(data || []));
    } catch (error) {
      console.error("PROJECT LOAD ERROR:", error);
      setProjects(getActiveProjects(getScopedCachedProjects(currentUser, directReportIds)));
    }
  }

  if (isDesktop) {
    return (
      <ReportBuilderDesktop
        onBack={onBack}
        reportScope={reportScope}
        onScope={setReportScope}
        scopeOptions={[
          { value: "my_time", label: "My Time" },
          { value: "crew_member", label: "Crew Member" },
          ...(showTeamReportScope || currentUser?.isGuest
            ? [{ value: "entire_team", label: "Entire Team" }]
            : []),
          ...(showOrgReportScope
            ? [{ value: "entire_organization", label: "Entire Organization" }]
            : []),
        ]}
        selectedEmployee={selectedEmployee}
        onEmployee={setSelectedEmployee}
        employees={employees}
        reportType={reportType}
        onReportType={(value) => {
          setReportType(value);
          setProject("");
          setStartDate("");
          setEndDate("");
        }}
        project={project}
        onProject={setProject}
        projects={projects}
        startDate={startDate}
        endDate={endDate}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        canGenerate={
          !!reportType &&
          !(reportType === "hours_by_project" && !project) &&
          !(reportType === "hours_by_date_range" && (!startDate || !endDate))
        }
        onGenerate={handleGenerateReport}
        isGenerating={isGenerating}
        reportResult={reportResult}
      />
    );
  }

  return (
    <div className="intel-screen">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="cockpit-header">
        {desktopNav ? (<DesktopBackLink onClick={onBack} label={desktopNav.parentLabel} />) : (
        <button
          className="cockpit-back-btn"
          onClick={onBack}
          aria-label="Back to Reports & Analytics"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 15, height: 15 }}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          
          <div>
            <div className="cockpit-title">HOURS REPORTS</div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: "1.5px",
                color: "var(--nx-text-3)",
                textTransform: "uppercase",
              }}
            >
              Report Builder
            </div>
          </div>
        </div>
      </div>

      {/* ── Section label ───────────────────────────────── */}
      <div style={{ padding: "22px 20px 0" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--nx-text-3)",
            borderLeft: "2px solid var(--nx-gold)",
            paddingLeft: 10,
            marginBottom: 4,
          }}
        >
          Configure Report
        </div>
      </div>

      {/* ── Builder card ─────────────────────────────────── */}
      <div className="rb-card">
        {/* Scope */}
        <div className="rb-field-group">
          <CPDropdown
            label="Scope"
            value={reportScope}
            onChange={setReportScope}
            includePlaceholder={false}
            options={[
              { value: "my_time", label: "My Time" },
              { value: "crew_member", label: "Crew Member" },
              ...(showTeamReportScope || currentUser?.isGuest
                ? [{ value: "entire_team", label: "Entire Team" }]
                : []),
              ...(showOrgReportScope
                ? [
                    {
                      value: "entire_organization",
                      label: "Entire Organization",
                    },
                  ]
                : []),
            ]}
          />
        </div>
        {reportScope === "crew_member" && (
          <div className="rb-field-group rb-animate-in">
            <CPDropdown
              label="Employee"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={[
                { value: "", label: "Select employee..." },
                ...employees.map((employee) => ({
                  value: employee.supabase_user_id,
                  label: employee.employee_name,
                })),
              ]}
            />
          </div>
        )}
        {/* Report Type */}
        <div className="rb-field-group">
          <CPDropdown
            label="Report Type"
            value={reportType}
            onChange={(value) => {
              setReportType(value);
              setProject("");
              setStartDate("");
              setEndDate("");
            }}
            options={[
              { value: "", label: "Select a report type…" },
              { value: "hours_by_project", label: "Hours By Project" },
              {
                value: "hours_by_date_range",
                label: "Hours By Date Range",
              },
            ]}
          />
        </div>

        {/* ── Conditional: Hours By Project ─────────────── */}
        {isHoursByProject && (
          <div className="rb-field-group rb-animate-in">
            <CPDropdown
              label="Project"
              value={project}
              onChange={setProject}
              options={[
                { value: "", label: "Select a project…" },
                // Placeholder options — replace with real data when backend is wired
                ...projects.map((p) => ({
                  value: p.project_id,
                  label: p.project_name,
                })),
              ]}
            />
          </div>
        )}

        {/* ── Conditional: Hours By Date Range ──────────── */}
        {isHoursByDateRange && (
          <>
            <div className="rb-field-group rb-animate-in">
              <CPInput
                label="Start Date"
                type="date"
                value={startDate}
                onChange={setStartDate}
              />
              {startDate && (
  <div
  style={{
    marginTop: "6px",
    color: "var(--nx-gold)",
    fontSize: "16px",
    fontWeight: "500",
  }}
>
    {new Date(
      startDate + "T12:00:00"
    ).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })}
  </div>
)}
            </div>
            <div className="rb-field-group rb-animate-in">
              <CPInput
                label="End Date"
                type="date"
                value={endDate}
                onChange={setEndDate}
              />
              {endDate && (
  <div
  style={{
    marginTop: "6px",
    color: "var(--nx-gold)",
    fontSize: "16px",
    fontWeight: "500",
  }}
>
    {new Date(
      endDate + "T12:00:00"
    ).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })}
  </div>
)}
            </div>
          </>
        )}

        {/* ── Divider ───────────────────────────────────── */}
        {reportType && <div className="rb-divider" />}

        {/* ── Generate button ───────────────────────────── */}
        <button
          className={`rb-generate-btn ${
            !reportType ||
            (reportType === "hours_by_project" && !project) ||
            (reportType === "hours_by_date_range" && (!startDate || !endDate))
              ? "rb-generate-btn-disabled"
              : ""
          }`}
          disabled={
            !reportType ||
            (reportType === "hours_by_project" && !project) ||
            (reportType === "hours_by_date_range" &&
              (!startDate || !endDate)) ||
            isGenerating
          }
          onClick={handleGenerateReport}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 16, height: 16 }}
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          Generate Report
        </button>
      </div>

      {reportResult && (
        <div className="rb-card">
          <div className="rb-field-label">{reportResult.type}</div>

          <div
            style={{
              color: "#FFFFFF",
              textTransform: "uppercase",
              fontSize: "12px",
              letterSpacing: "1px",
              marginBottom: "6px",
            }}
          >
            {reportResult.type === "Hours By Project"
  ? "Project"
  : "Date Range"}
          </div>

          <h3
            style={{
              color: "white",
              marginBottom: "20px",
            }}
          >
            {reportResult.title}
          </h3>

          <div
            style={{
              color: "#FFFFFF",
              textTransform: "uppercase",
              fontSize: "12px",
              letterSpacing: "1px",
              marginBottom: "6px",
            }}
          >
            Total Project Hours
          </div>

          <p
            style={{
              color: "var(--nx-gold)",
              fontSize: "32px",
              fontWeight: "700",
              marginBottom: "8px",
            }}
          >
            {formatHoursMinutes(
  reportResult.totalHours
)}
          </p>

          <p
            style={{
              color: "#8fa7d8",
              marginBottom: "20px",
            }}
          >
            Entries: {reportResult.entries}
          </p>

          {reportResult.grouped && (
            <>
              <div
                style={{
                  color: "var(--nx-text-2)",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  marginBottom: "12px",
                  letterSpacing: "1px",
                }}
              >
                Project Breakdown
              </div>

              {Object.entries(reportResult.grouped).map(
                ([projectName, hours]) => (
                  <div
                    key={projectName}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "10px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <span style={{ color: "white" }}>{projectName}</span>

                    <span
                      style={{
                        color: "var(--nx-gold)",
                        fontWeight: 600,
                      }}
                    >
                      {formatHoursMinutes(hours)}
                    </span>
                  </div>
                ),
              )}
            </>
          )}

          {reportResult.logs && (
            <>
              <div
                style={{
                  color: "var(--nx-text-2)",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  marginTop: "16px",
                  marginBottom: "12px",
                  letterSpacing: "1px",
                }}
              >
                Detailed Entries
              </div>

              {reportResult.logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    padding: "10px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div style={{ color: "white" }}>
  {new Date(log.date_iso + "T12:00:00").toLocaleDateString(
    "en-US",
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    }
  )}
</div>

                  <div style={{ color: "white" }}>
                    Employee: {log.employee || "N/A"}
                  </div>

                  <div style={{ color: "white" }}>
                    Task: {log.work_completed || "N/A"}
                  </div>
                  <div style={{ color: "#8db8ff" }}>
                    Shift:{" "}
                    {log.start_time && log.end_time
                      ? formatSessionShiftLabel(log.start_time, log.end_time)
                      : "N/A"}
                  </div>

                  <div
                    style={{
                      color: "var(--nx-text-3)",
                      fontSize: "12px",
                    }}
                  >
                    Work Type: {log.work_category || "N/A"}
                  </div>

                  <div
                    style={{
                      color: "var(--nx-text-2)",
                      fontSize: "14px",
                    }}
                  >
                    {log.project_name}
                  </div>

                  <>
                    <div
                      style={{
                        color: "var(--nx-gold)",
                        marginTop: "4px",
                      }}
                    >
                      Logged: {formatHoursMinutes(
  log.total_hours
)}
                    </div>

                    <div
                      style={{
                        color: "var(--nx-text-2)",
                        fontSize: "13px",
                        marginTop: "2px",
                      }}
                    >
                      Gross:{" "}
                      {(Number(log.total_seconds || 0) / 3600).toFixed(2)} hrs
                    </div>

                    <div
                      style={{
                        color: "var(--nx-text-2)",
                        fontSize: "13px",
                      }}
                    >
                      Break: {Math.round(Number(log.break_seconds || 0) / 60)}{" "}
                      min
                    </div>

                    <div
                      style={{
                        color: "var(--nx-text-2)",
                        fontSize: "13px",
                      }}
                    >
                      Net:{" "}
                      {(Number(log.net_work_seconds || 0) / 3600).toFixed(2)}{" "}
                      hrs
                    </div>
                  </>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ── Empty state hint ─────────────────────────────── */}
      {!reportType && (
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--nx-text-3)",
            textAlign: "center",
            letterSpacing: "0.08em",
            padding: "0 24px",
            marginTop: 8,
          }}
        >
          Select a report type above to configure your report.
        </p>
      )}
    </div>
  );
}
